import React, { useState, useEffect, useRef, useCallback } from "react";
import styled from "styled-components";
import Cookies from "js-cookie";
import { useChat } from "./ChatContext";
import { FaThumbtack, FaTrashAlt } from "react-icons/fa"; // Add trash icon
import { useAuth } from "../AuthContext";
import { format } from "date-fns";
import { ChatBubble, ChatMessageGroup, ChatDateDivider } from "./ChatBubble";
import { IoMdSend } from "react-icons/io";
import Typography from '@mui/material/Typography';
import { 
  Tooltip, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogContentText, 
  DialogTitle, 
  Button,
  Box,
  IconButton
} from '@mui/material';
import AddGroupParticipants from './AddGroupParticipants';
import GroupAddIcon from '@mui/icons-material/GroupAdd';

const CHAT_WIDTH = 400;
const STATUS_BAR_HEIGHT = 40;

const ChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: ${props => props.isInSocial ? '100%' : `${CHAT_WIDTH}px`};
  height: ${props => props.isInSocial ? '100%' : `calc(100vh - ${STATUS_BAR_HEIGHT}px)`};
  background: ${props => props.isInSocial ? 'transparent' : '#2d2f33'};
  color: white;
  box-shadow: ${props => props.isInSocial ? 'none' : '-2px 0 10px rgba(0, 0, 0, 0.3)'};
  padding: 1rem;
  box-sizing: border-box;
  position: ${props => props.isInSocial ? 'relative' : 'fixed'};
  bottom: ${props => props.isInSocial ? '0' : `${STATUS_BAR_HEIGHT-6}px`};
  right: 0;
  z-index: ${props => props.isInSocial ? '1' : '1000'};
  transition: ${props => props.isInSocial ? 'none' : 'right 0.3s ease-in-out'};
`;

const LockButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  background: none;
  border: none;
  color: ${(props) => (props.isChatLocked ? "#ff4747" : "#4a9bfc")};
  cursor: pointer;
  font-size: 14px;
  padding: 4px;
  transition: color 0.2s ease-in-out;

  &:hover {
    color: ${(props) => (props.isChatLocked ? "#e33a3a" : "#3580e3")};
  }
`;

// Add a new styled component for the chat header
const ChatHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 0.75rem;
  background: #2d2f33;
  border-radius: ${props => props.isInSocial ? '8px 8px 0 0' : '8px 8px 0 0'};
  margin-bottom: 0.5rem;
  position: relative;
`;

const ChatTitle = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: ${props => props.hasAvatar ? '0.75rem' : '0'};
`;

const ChatAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: #4a9bfc;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 500;
`;

const OnlineIndicator = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${props => props.isOnline ? '#4caf50' : '#666'};
  position: absolute;
  bottom: 0;
  right: 0;
  border: 2px solid #2d2f33;
`;

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0.5rem;
  background: ${props => props.isInSocial ? '#1c1e22' : '#1c1e22'};
  border-radius: ${props => props.isInSocial ? '0 8px 8px 0' : '8px'};
  display: flex;
  flex-direction: column;
  margin-top: ${props => props.isInSocial ? '0' : '2rem'};
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: #1c1e22;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.15);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.25);
  }
  
  /* For Firefox */
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.15) #1c1e22;
`;

const InputContainer = styled.div`
  display: flex;
  border-top: 1px solid #444;
  padding: 10px;
  background: #2d2f33;
`;

const ChatInput = styled.input`
  flex: 1;
  padding: 0.5rem;
  border: none;
  border-radius: 5px;
  background: #1e2125;
  color: white;
  outline: none;
  margin-right: 0.5rem;
`;

const SendButton = styled.button`
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 5px;
  background: #4a9bfc;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;

  &:hover {
    background: #3a8bec;
  }

  &:disabled {
    background: #555;
    cursor: not-allowed;
  }
`;

// Add a new styled component for the delete button
const DeleteButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  background: none;
  border: none;
  color: #9e9e9e;
  cursor: pointer;
  font-size: 14px;
  padding: 4px;
  transition: color 0.2s ease-in-out;

  &:hover {
    color: #ff4747;
  }
`;

const Chat = ({ isInSocial }) => {
  const { 
    isChatOpen, 
    setIsChatOpen, 
    isChatLocked, 
    lockChat, 
    activeChat,
    deletePrivateChat,
    leaveGroup,
    switchChat // Add switchChat from context
  } = useChat();
  const { user } = useAuth();
  const chatRef = useRef(null);
  const messagesEndRef = useRef(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [currentGroupId, setCurrentGroupId] = useState(null);
  const [error, setError] = useState(null);
  const [cachedMessages, setCachedMessages] = useState({});
  const [isFetching, setIsFetching] = useState(false);
  const [pendingMessages, setPendingMessages] = useState([]);
  const [requestId, setRequestId] = useState(0);
  // Add state for delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  // Add new state for adding participants
  const [showAddParticipants, setShowAddParticipants] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [lastMessageId, setLastMessageId] = useState(null);
  const messagesContainerRef = useRef(null);

  // Fix API URL - ensure it includes the correct path structure
  const apiUrl = process.env.REACT_APP_API_URL;

  // Update currentGroupId whenever activeChat changes
  useEffect(() => {
    if (activeChat && activeChat.id) {
      setCurrentGroupId(activeChat.id);
    }
  }, [activeChat]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (chatRef.current && !chatRef.current.contains(event.target) && !isChatLocked) {
        setIsChatOpen(false);
      }
    };

    if (isChatOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isChatOpen, isChatLocked]);

const fetchMessages = async (currentRequestId, signal) => {
  // Hent lagret chat hvis activeChat er null
  const chatToUse = activeChat || JSON.parse(localStorage.getItem('activeChat'));
  
  if (!chatToUse?.id || !currentGroupId || chatToUse.id !== currentGroupId) {
    console.error("Invalid chat state");
    return;
  }

  if (isFetching) return;
  setIsFetching(true);

  try {
    const response = await fetch(`${apiUrl}/api/chat/get_messages`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${Cookies.get("token")}`,
      },
      body: JSON.stringify({
        group_id: chatToUse.id,
        chat_type: chatToUse.type,
        request_id: currentRequestId // Send request ID to backend
      }),
      signal // Pass AbortController signal
    });

    // Verify we're still in the same chat
    if (!activeChat || activeChat.id !== currentGroupId) {
      console.warn("Chat changed during fetch, discarding results");
      return;
    }

    const data = await response.json();

    // Additional validation of response data
    if (!data.messages || !Array.isArray(data.messages)) {
      console.error("Invalid message format received:", data);
      setError("Invalid message format received from server");
      return;
    }

    // Verify chat type matches
    if (data.chat_type !== activeChat.type) {
      console.error("Chat type mismatch:", {
        expected: activeChat.type,
        received: data.chat_type
      });
      setError("Chat type mismatch - reloading messages");
      return;
    }

    // Messages come in chronological order (oldest first)
    setMessages(data.messages);
    setCachedMessages(prev => ({
      ...prev,
      [activeChat.id]: data.messages
    }));

    // Scroll to bottom on new messages
    scrollToBottom();
  } catch (error) {
    if (error.name === 'AbortError') {
      console.log("Fetch aborted due to chat switch");
      return;
    }
    console.error("Error fetching messages:", error);
    setError("Failed to load messages");
  } finally {
    setIsFetching(false);
  }
};

  useEffect(() => {
    if (currentGroupId && isChatOpen) {
      const newRequestId = requestId + 1;
      setRequestId(newRequestId);
      fetchMessages(newRequestId);
      const interval = setInterval(() => fetchMessages(newRequestId), 5000);
      return () => clearInterval(interval);
    }
  }, [currentGroupId, isChatOpen]);

  // Add effect to reset messages and fetch new ones when activeChat changes
  useEffect(() => {
    if (activeChat) {
      const maybeCached = cachedMessages[activeChat.id] || [];
      setMessages(maybeCached);
      setCurrentGroupId(activeChat.id);
      // Removed the redundant fetchMessages() call here
      scrollToBottom();
      console.log("Active chat changed to:", activeChat.name);
    }
  }, [activeChat]);

  // Update messages only when pendingMessages is ready
  useEffect(() => {
    if (activeChat && activeChat.id === currentGroupId && pendingMessages.length) {
      setMessages(pendingMessages);
      setPendingMessages([]);
    }
  }, [pendingMessages, activeChat, currentGroupId]);

  // Add debugging to understand how messages are sent
  const sendMessage = async () => {
    const messageText = input.trim();
    
    // Add strict validation of active chat
    if (!activeChat?.id || !currentGroupId || activeChat.id !== currentGroupId) {
      console.error("Chat synchronization error", {
        activeChatId: activeChat?.id,
        currentGroupId,
        chatType: activeChat?.type
      });
      setError("Chat synchronization error. Please reload the chat.");
      return;
    }
  
    console.log("Attempting to send message:", {
      to_chat_id: activeChat.id,
      chat_type: activeChat.type,
      current_group: currentGroupId
    });
  
    if (!messageText) return;
  
    setIsSending(true);
    setError(null);
  
    try {
      const endpoint = `${apiUrl}/api/chat/send_message`;
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${Cookies.get("token")}`,
        },
        body: JSON.stringify({
          message: messageText,
          group_id: activeChat.id,
          chat_type: activeChat.type // Add chat type for backend validation
        }),
      });
  
      if (!response.ok) {
        throw new Error(`Server returned ${response.status}`);
      }
  
      setInput("");
      // Immediately fetch new messages to ensure we see our message
      await fetchMessages(requestId);
      scrollToBottom();
    } catch (error) {
      console.error("Failed to send message:", error);
      setError(`Failed to send message: ${error.message}`);
    } finally {
      setIsSending(false);
    }
  };

  // Make sure the Enter key handler definitely works
  const handleKeyDown = (e) => {
    console.log("KeyDown event:", e.key);
    if (e.key === "Enter" && !e.shiftKey && !isSending && activeChat && input.trim()) {
      console.log("Enter key pressed - sending message");
      e.preventDefault(); // Prevent default to avoid form submission
      sendMessage();
    }
  };

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  
  // Format messages into groups by date and consecutive sender
  const renderMessages = () => {
    if (!messages.length) return null;
    
    let currentDate = '';
    let currentSender = '';
    let messageGroups = [];
    let currentGroup = [];
    
    messages.forEach((msg, index) => {
      const messageDate = new Date(msg.timestamp || msg.date || new Date());
      const dateStr = format(messageDate, 'yyyy-MM-dd');
      const timeStr = format(messageDate, 'HH:mm');
      const isSent = user && msg.username === user.username;
      
      // Check if this is a new day
      if (dateStr !== currentDate) {
        // Add the previous group if it exists
        if (currentGroup.length > 0) {
          messageGroups.push({
            sender: currentSender,
            messages: [...currentGroup],
            isSent: currentGroup[0].isSent
          });
          currentGroup = [];
        }
        
        // Add date divider
        messageGroups.push({
          type: 'date',
          date: dateStr
        });
        
        currentDate = dateStr;
        currentSender = msg.username;
      }
      
      // Check if this is a new sender
      if (msg.username !== currentSender) {
        // Add the previous group if it exists
        if (currentGroup.length > 0) {
          messageGroups.push({
            sender: currentSender,
            messages: [...currentGroup],
            isSent: currentGroup[0].isSent
          });
          currentGroup = [];
        }
        
        currentSender = msg.username;
      }
      
      // Add this message to the current group
      currentGroup.push({
        ...msg,
        time: timeStr,
        isSent,
        isConsecutive: currentGroup.length > 0,
        message_type: msg.message_type || 'text'
      });
      
      // If this is the last message, add the group
      if (index === messages.length - 1 && currentGroup.length > 0) {
        messageGroups.push({
          sender: currentSender,
          messages: [...currentGroup],
          isSent: currentGroup[0].isSent
        });
      }
    });
    
    return messageGroups.map((group, groupIndex) => {
      if (group.type === 'date') {
        return (
          <ChatDateDivider 
            key={`date-${groupIndex}`}
            date={format(new Date(group.date), 'MMMM d, yyyy')} 
          />
        );
      }
      
      return (
        <ChatMessageGroup key={`group-${groupIndex}`} isSent={group.isSent}>
          {group.messages.map((msg, msgIndex) => (
            <ChatBubble
              key={`msg-${group.sender}-${msgIndex}`}
              message={msg.message}
              sender={msg.username}
              time={msg.time}
              isSent={msg.isSent}
              consecutive={msg.isConsecutive}
              showSender={msgIndex === 0 && !msg.isSent}
              showTime={msgIndex === group.messages.length - 1}
              isEdited={msg.is_edited}
              messageType={msg.message_type}
            />
          ))}
        </ChatMessageGroup>
      );
    });
  };

  useEffect(() => {
    // Log the environment variable
    console.log("Using API URL:", apiUrl);
    // ...existing code...
  }, []);

  // Helper function to get the chat name with better type checking
const getChatDisplayName = () => {
  if (!activeChat) return '';
  
  console.log("Getting display name for:", activeChat);
  
  // For group chats, just use the name
  if (activeChat.type === 'group' || activeChat.type === 'channel') {
    return activeChat.name || 'Unnamed Group';
  }
  
  // For private chats, find the other participant
  if (activeChat.type === 'one-to-one' && activeChat.participants && Array.isArray(activeChat.participants)) {
    const currentUser = user?.username;
    
    // If this is a self-chat
    if (activeChat.participants.length === 1) {
      return 'Notes to Self';
    }
    
    const otherUser = activeChat.participants.find(p => p !== currentUser);
    return otherUser || activeChat.name || 'Unknown User';
  }
  
  // Fallback
  return activeChat.name || 'Unknown Chat';
};

  // Determine if the other participant is online
  const isOtherParticipantOnline = () => {
    // Only relevant for private chats
    if (!activeChat || !activeChat.participants || activeChat.participants.length !== 2) {
      return false;
    }
    
    const currentUser = user?.username;
    const otherUser = activeChat.participants.find(p => p !== currentUser);
    
    // We'd need to check against the online users here
    // This is a placeholder - you would need to implement
    // the logic to check if the other user is online
    return false;
  };

  // Get initials for the avatar
  const getAvatarInitials = () => {
    const name = getChatDisplayName();
    if (!name) return '?';
    
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Function to handle chat deletion
  const handleDeleteChat = async () => {
    if (!activeChat) return;
    
    try {
      setError(null);
      let success = false;
      
      // Show a temporary "deleting" message
      setError(`${activeChat.type === 'one-to-one' ? 'Deleting' : 'Leaving'} conversation...`);
      
      if (activeChat.type === 'one-to-one') {
        console.log(`Attempting to delete private chat with ID: ${activeChat.id}`);
        success = await deletePrivateChat(activeChat.id);
      } else if (activeChat.type === 'group' || activeChat.type === 'channel') {
        console.log(`Attempting to leave group with ID: ${activeChat.id}`);
        success = await leaveGroup(activeChat.id);
      }
      
      if (success) {
        console.log("Chat deletion/leave successful");
        // Close the dialog
        setDeleteDialogOpen(false);
        // Clear error message
        setError(null);
        // No need to clear activeChat here as it's already handled in ChatContext
      } else {
        console.error("Chat deletion/leave returned false");
        setError(`Failed to ${activeChat.type === 'one-to-one' ? 'delete' : 'leave'} conversation. Please try again.`);
      }
    } catch (err) {
      console.error("Error in handleDeleteChat:", err);
      setError(`Error: ${err.message || 'Unknown error occurred'}`);
    }
  };

  // Open delete confirmation dialog
  const openDeleteDialog = (e) => {
    e.stopPropagation();
    setDeleteDialogOpen(true);
  };

  // Close delete confirmation dialog
  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
  };

  // Function to handle adding participants to the group
  const handleParticipantsAdded = (addedUsers) => {
    // Refresh messages to show the system messages about new members
    fetchMessages(requestId);
  };

  // Fetch initial messages
  const fetchInitialMessages = async () => {
    setIsFetching(true);
    const token = Cookies.get("token");
    if (!token || !currentGroupId) return;

    try {
      setError(null);
      const endpoint = `${apiUrl}/api/chat/get_messages`;
      
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json", 
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ 
          limit: 50,
          offset: 0,
          group_id: currentGroupId
        }),
      });

      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data.messages)) {
          setMessages(data.messages.reverse());
          setHasMore(data.has_more);
          setOffset(data.next_offset || 0);
          if (data.messages.length > 0) {
            setLastMessageId(data.messages[0].message_id);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching messages:", error);
      setError(`Error fetching messages: ${error.message}`);
    } finally {
      setIsFetching(false);
    }
  };

  // Fetch more messages when scrolling up
  const fetchMoreMessages = async () => {
    if (isLoadingMore || !hasMore || !lastMessageId) return;
    
    setIsLoadingMore(true);
    const token = Cookies.get("token");

    try {
      const endpoint = `${apiUrl}/api/chat/get_messages`;
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json", 
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ 
          limit: 50,
          group_id: currentGroupId,
          last_message_id: lastMessageId
        }),
      });

      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data.messages)) {
          const newMessages = data.messages.reverse();
          setMessages(prev => [...newMessages, ...prev]);
          setHasMore(data.has_more);
          if (newMessages.length > 0) {
            setLastMessageId(newMessages[0].message_id);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching more messages:", error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Handle scroll events for infinite scroll
  const handleScroll = useCallback(() => {
    if (!messagesContainerRef.current) return;
    
    const { scrollTop } = messagesContainerRef.current;
    if (scrollTop === 0 && hasMore) {
      fetchMoreMessages();
    }
  }, [hasMore, fetchMoreMessages]);

  // Add scroll event listener
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  // Reset state when changing chats
  useEffect(() => {
    if (activeChat) {
      setOffset(0);
      setHasMore(true);
      setLastMessageId(null);
      setMessages([]);
      fetchInitialMessages();
    }
  }, [activeChat]);

  // Replace these two separate useEffects with a consolidated one
  useEffect(() => {
    if (activeChat && activeChat.id) {
      // Clear any existing messages first to avoid showing previous chat content
      setMessages([]);
      
      // Set the current group ID
      setCurrentGroupId(activeChat.id);
      
      // Log the change for debugging
      console.log(`Chat changed to: ${activeChat.name} (ID: ${activeChat.id})`);
      
      // Check for cached messages
      const maybeCached = cachedMessages[activeChat.id] || [];
      if (maybeCached.length > 0) {
        setMessages(maybeCached);
        scrollToBottom();
      } else if (isChatOpen) {
        // Only fetch if the chat panel is open
        const newRequestId = requestId + 1;
        setRequestId(newRequestId);
        console.log(`Initiating fetch for messages in group: ${activeChat.id} (RequestID: ${newRequestId})`);
        fetchMessages(newRequestId);
      }
    }
  }, [activeChat]); // Only depend on activeChat changing

  // This effect handles periodic refreshes when the chat is open
  useEffect(() => {
    // Only set up the interval if we have an active chat and the panel is open
    if (activeChat?.id && isChatOpen) {
      console.log(`Setting up refresh interval for chat: ${activeChat.id}`);
      const currentReqId = requestId;
      const interval = setInterval(() => fetchMessages(currentReqId), 5000);
      
      return () => {
        console.log(`Clearing refresh interval for chat: ${activeChat.id}`);
        clearInterval(interval);
      };
    }
  }, [activeChat, isChatOpen]);

  // Add debugging when switching chats
useEffect(() => {
  if (activeChat && activeChat.id) {
    console.log("Switch to chat:", {
      id: activeChat.id,
      type: activeChat.type,
      name: activeChat.name,
      participants: activeChat.participants
    });
    
    if (!activeChat.type) {
      console.warn("Chat is missing type property:", activeChat);
    }
    
    // ...rest of the existing code...
  }
}, [activeChat]);

useEffect(() => {
  if (activeChat && activeChat.id) {
    console.log("Chat component: Active chat changed to:", {
      id: activeChat.id,
      type: activeChat.type,
      name: activeChat.name,
      currentGroupId
    });

    // Verifiser at vi har riktig gruppe-ID før vi setter den
    if (currentGroupId !== activeChat.id) {
      console.log(`Updating currentGroupId from ${currentGroupId} to ${activeChat.id}`);
      setCurrentGroupId(activeChat.id);
    }

    // Clear existing messages when switching chats
    setMessages([]);
    
    // Reset pagination state
    setOffset(0);
    setHasMore(true);
    setLastMessageId(null);

    // Increment request ID to cancel any pending fetches
    setRequestId(prev => prev + 1);
  }
}, [activeChat]);

  useEffect(() => {
    if (!isChatOpen && !isChatLocked) {
      // Når chatten lukkes, fjern fra localStorage
      localStorage.removeItem('activeChat');
    }
  }, [isChatOpen, isChatLocked]);

  return (
    <ChatContainer ref={chatRef} isInSocial={isInSocial}>
      {/* Only show lock button if not in social route */}
      {!isInSocial && (
        <LockButton onClick={lockChat} isChatLocked={isChatLocked}>
          <FaThumbtack />
        </LockButton>
      )}

      {/* Add chat header */}
      {activeChat && (
        <ChatHeader isInSocial={isInSocial}>
          <ChatAvatar>
            {getAvatarInitials()}
            {activeChat.participants && activeChat.participants.length === 2 && (
              <OnlineIndicator isOnline={isOtherParticipantOnline()} />
            )}
          </ChatAvatar>
          <ChatTitle hasAvatar={true}>
            <Typography variant="subtitle1" sx={{ color: '#e0e0e0', fontWeight: 500 }}>
              {getChatDisplayName()}
            </Typography>
            <Typography variant="caption" sx={{ color: '#9e9e9e' }}>
              {activeChat.type === 'one-to-one' 
                ? (isOtherParticipantOnline() ? 'Online' : 'Offline') 
                : `${activeChat.participants?.length || 0} participants`}
            </Typography>
          </ChatTitle>
          
          {/* Add Group Action Buttons - Add this section */}
          <Box sx={{ display: 'flex', mr: 4 }}>
            {/* Only show add participants button for group chats */}
            {activeChat.type !== 'one-to-one' && (
              <Tooltip title="Add participants">
                <IconButton 
                  size="small" 
                  onClick={() => setShowAddParticipants(true)}
                  sx={{ 
                    color: '#9e9e9e',
                    mr: 1,
                    '&:hover': { color: '#4a9bfc' } 
                  }}
                >
                  <GroupAddIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          
          {/* Delete Conversation Button */}
          <Tooltip title={activeChat.type === 'one-to-one' ? "Delete conversation" : "Leave group"}>
            <DeleteButton onClick={openDeleteDialog}>
              <FaTrashAlt />
            </DeleteButton>
          </Tooltip>
        </ChatHeader>
      )}
      
      {/* Add participants overlay */}
      {showAddParticipants && activeChat && (
        <Box 
          sx={{ 
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 10,
            backgroundColor: '#2d2f33',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
          <AddGroupParticipants 
            group={activeChat}
            onClose={() => setShowAddParticipants(false)} 
            onUsersAdded={handleParticipantsAdded}
          />
        </Box>
      )}
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          {activeChat?.type === 'one-to-one' ? "Delete Conversation" : "Leave Group"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            {activeChat?.type === 'one-to-one' 
              ? "Are you sure you want to delete this conversation? This action cannot be undone."
              : "Are you sure you want to leave this group? You'll need to be added back to rejoin."}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteChat} color="error" autoFocus>
            {activeChat?.type === 'one-to-one' ? "Delete" : "Leave"}
          </Button>
        </DialogActions>
      </Dialog>

      <MessagesContainer 
        ref={messagesContainerRef} 
        isInSocial={isInSocial}
      >
        {isLoadingMore && (
          <Box sx={{ 
            textAlign: 'center', 
            py: 2, 
            color: '#9e9e9e',
            position: 'relative',
            zIndex: 1 
          }}>
            Loading more messages...
          </Box>
        )}
        {activeChat ? (
          <>
            {error && (
              <div style={{ 
                color: '#ff4747', 
                padding: '10px', 
                margin: '10px 0', 
                backgroundColor: 'rgba(255, 71, 71, 0.1)',
                borderRadius: '4px',
                textAlign: 'center'
              }}>
                {error}
              </div>
            )}
            {isFetching && !messages.length ? (
              <div style={{ textAlign: 'center', padding: '20px', color: '#9e9e9e' }}>
                Loading messages...
              </div>
            ) : messages.length === 0 ? (
              <div style={{ 
                textAlign: 'center', 
                padding: '20px',
                color: '#9e9e9e' 
              }}>
                No messages yet. Start the conversation!
              </div>
            ) : (
              <div className="messages-wrapper" style={{ paddingBottom: '12px' }}>
                {renderMessages()}
              </div>
            )}
            <div ref={messagesEndRef} />
          </>
        ) : (
          <div style={{ 
            textAlign: 'center', 
            padding: '20px',
            color: '#9e9e9e' 
          }}>
            Select a conversation to start chatting
          </div>
        )}
      </MessagesContainer>

      <InputContainer>
        <ChatInput
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={activeChat ? "Type your message..." : "Select a conversation first"}
          // Endret onKeyDown for å logge nøkkeltrykket og så kalle handleKeyDown
          onKeyDown={(e) => {
            console.log("onKeyDown triggered:", e.key);
            handleKeyDown(e);
          }}
        />
        <SendButton onClick={sendMessage} disabled={isSending}>
          <IoMdSend />
        </SendButton>
      </InputContainer>
    </ChatContainer>
  );
};

export default Chat;